import * as React from 'react';
import {Mutation} from 'react-apollo';

import type {EntityCustomFieldType} from 'nutshell-graphql-types';
import type {
    EntityAttribute as EntityAttributeFragment,
    EntityAttributeValue as EntityAttributeValueFragment,
    UpdateLeadInputFieldInput,
    UpdateLeadCustomFieldValue as UpdateLeadCustomFieldValueMutation,
    UpdateLeadCustomFieldValueVariables as UpdateLeadCustomFieldValueMutationVariables,
    CustomFieldValueInput,
} from 'nutshell-graphql-types';

import {GraphQLFieldDate} from '../../../field/graphql-field-date';
import {GraphQLFieldEnumMultiple} from '../../../field/graphql-field-enum-multiple';
import {GraphQLFieldEnumSingle} from '../../../field/graphql-field-enum-single';
import {GraphQLFieldString} from '../../../field/graphql-field-string';
import {GraphQLFieldText} from '../../../field/graphql-field-text';

import UPDATE_LEAD_CUSTOM_FIELD_VALUE from '../../shared/graphql/mutations/update-lead-custom-field-value.graphql';

import {getCustomFieldValueForMutation} from './get-custom-field-value-for-mutation';

import styles from './condition-custom-field.module.css';

type OwnProps = {
    value: EntityAttributeValueFragment | null | undefined;
    field: EntityAttributeFragment;
    conditionText: string;
    entityId: string;
};

type State = {
    value: CustomFieldValueInput;
    blinkSuccess: boolean;
};

type Props = OwnProps & {
    updateLead: (arg1: UpdateLeadInputFieldInput) => Promise<any>;
};

export class ConditionCustomFieldComponent extends React.PureComponent<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            value: getCustomFieldValueForMutation(props.value, props.field.attribute.type),
            blinkSuccess: false,
        };
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        if (JSON.stringify(prevState.value) !== JSON.stringify(this.state.value)) {
            // Anytime an ENUM or DATE value changes, we want to auto-submit the form
            if (
                this.props.field.attribute.type === 'ENUM' ||
                this.props.field.attribute.type === 'DATE'
            ) {
                this.submitForm();
            }
        }

        // If we've just saved successfully, wait 2 seconds and then clear
        // our successful save state.
        if (!prevState.blinkSuccess && this.state.blinkSuccess) {
            setTimeout(() => {
                this.setState({blinkSuccess: false});
            }, 2000);
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (nextProps.value !== this.props.value) {
            this.setState({
                value: getCustomFieldValueForMutation(
                    nextProps.value,
                    nextProps.field.attribute.type
                ),
            });
        }
    }

    render() {
        let field;

        // Assigning this variable and typing it here allows our
        // switch/case statement to strongly type our enum.
        //
        // It should work without this assignment, but it doesn't :(
        const type: EntityCustomFieldType = this.props.field.attribute.type;

        switch (type) {
            case 'STRING':
                field = (
                    <GraphQLFieldString
                        value={this.state.value.string}
                        onChange={this.handleChange}
                        onCancel={this.handleCancel}
                        onSubmit={this.submitForm}
                        isDirty={this.isInputDirty()}
                        blinkSuccess={this.state.blinkSuccess}
                        label={this.props.conditionText}
                        field={this.props.field}
                    />
                );
                break;
            case 'TEXT':
                {
                    field = (
                        <GraphQLFieldText
                            value={this.state.value.text}
                            onChange={this.handleChange}
                            onCancel={this.handleCancel}
                            onSubmit={this.submitForm}
                            isDirty={this.isInputDirty()}
                            blinkSuccess={this.state.blinkSuccess}
                            label={this.props.conditionText}
                            field={this.props.field}
                        />
                    );
                }
                break;
            case 'ENUM':
                {
                    // Multiple choice fields are arrays, so we'll show
                    // a different type of form field component here
                    // @ts-expect-error
                    if (this.props.field.allowMultiple) {
                        field = (
                            <GraphQLFieldEnumMultiple
                                value={this.state.value.enumMultiple}
                                onChange={this.handleChange}
                                menuPortalTarget={document.body || undefined}
                                label={this.props.conditionText}
                                styles={{
                                    // @ts-expect-error
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 5,
                                    }),
                                }}
                                // @ts-expect-error
                                field={this.props.field}
                            />
                        );
                    } else {
                        field = (
                            <GraphQLFieldEnumSingle
                                value={this.state.value.enumSingle}
                                onChange={this.handleChange}
                                blinkSuccess={this.state.blinkSuccess}
                                // null fails flow, so fall back to undefined
                                menuPortalTarget={document.body || undefined}
                                label={this.props.conditionText}
                                styles={{
                                    // @ts-expect-error
                                    menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 5,
                                    }),
                                }}
                                // @ts-expect-error
                                field={this.props.field}
                            />
                        );
                    }
                }
                break;
            case 'DATE': {
                field = (
                    <GraphQLFieldDate
                        label={this.props.conditionText}
                        value={this.state.value.date}
                        fieldName={this.props.field.attribute.name}
                        blinkSuccess={this.state.blinkSuccess}
                        onChange={this.handleChange}
                    />
                );
                break;
            }
            default:
                field = (
                    <a
                        onClick={this.hackyScrollAndFocusSidebarCustomField}
                        href={this.getCustomFieldId()}
                        className={styles['default-custom-field']}
                    >
                        {this.props.conditionText}
                    </a>
                );
                break;
        }

        return <div className={styles['condition-custom-field']}>{field}</div>;
    }

    // Ids cannot have spaces, so we remove all whitespace when setting
    // the id in the html.
    getCustomFieldId = () => {
        const customFieldNameNoWhiteSpace = this.props.field.attribute.name.replace(/\s/g, '');

        // This is the full id in the browser.
        return `#custom-field-${customFieldNameNoWhiteSpace}`;
    };

    hackyScrollAndFocusSidebarCustomField = () => {
        const customFieldName = this.getCustomFieldId();

        // Attempt to find eipElement with jQuery. This needs to be jQuery
        // because we access the actual eip class from jQuery's `.data()` method
        const eipElement = $(escapeForJquery(customFieldName));

        // If we found our element, programatically trigger a click event
        if (eipElement) {
            // We need this timeout because our initial click event
            // will natively scroll the page down to our custom field,
            // but it will then treat it as a "blur" event on the EIP element
            // if we've already clicked on it.
            setTimeout(() => {
                eipElement.click();
            }, 100);
        }
    };

    handleChange = (newVal: CustomFieldValueInput) => {
        this.setState({
            value: newVal,
        });
    };

    handleCancel = () => {
        this.setState({
            value: getCustomFieldValueForMutation(
                this.props.value,
                this.props.field.attribute.type
            ),
        });
    };

    submitForm = () => {
        if (this.isInputDirty()) {
            this.props
                .updateLead({
                    id: this.props.entityId,
                    customFields: [
                        {
                            fieldName: this.props.field.attribute.name,
                            value: this.state.value,
                        },
                    ],
                })
                .then(() => {
                    Lead.reloadCustomFields();
                });

            // This makes the text go green to indicate a successful
            // server save. This is currently optimisic, without an error
            // state.
            this.setState({blinkSuccess: true});
        }
    };

    isInputDirty = () => {
        const initialValue = getCustomFieldValueForMutation(
            this.props.value,
            this.props.field.attribute.type
        );

        return JSON.stringify(this.state.value) !== JSON.stringify(initialValue);
    };
}

class UpdateCustomFieldMutation extends Mutation<
    UpdateLeadCustomFieldValueMutation,
    UpdateLeadCustomFieldValueMutationVariables
> {}

export const ConditionCustomField = (props: OwnProps) => (
    <UpdateCustomFieldMutation mutation={UPDATE_LEAD_CUSTOM_FIELD_VALUE}>
        {(updateLead) => (
            <ConditionCustomFieldComponent
                {...props}
                updateLead={(payload) =>
                    updateLead({
                        variables: {
                            lead: {
                                id: payload.id,
                                customFields: payload.customFields,
                            },
                        },
                    })
                }
            />
        )}
    </UpdateCustomFieldMutation>
);

// jQuery can't handle special characters, which we can use to create the id
// This is a modified form of https://learn.jquery.com/using-jquery-core/faq/how-do-i-select-an-element-by-an-id-that-has-characters-used-in-css-notation/
// @ts-expect-error
function escapeForJquery(myid) {
    return `${myid.replace(/(\?:|\.|\[|\]|,|=|@|\?)/g, '\\$1')}`;
}
